// Centralized UI-string translation for the admin portal (take-order-i18n spec).
//
// Mirrors admin-mobile-app/src/utils/i18n.js, and structurally mirrors
// utils/money.js: module-level state, one setter, pure readers. Deliberately
// hand-rolled with NO npm dependency (Requirement 13.6) — react-i18next and
// react-intl would each need adding to two different build systems (CRA here,
// Metro/Expo on mobile) for ~48 strings with no plural or gender complexity.
//
// TWO DESIGN CONSTRAINTS SHAPE THIS FILE:
//
// 1. `t()` MUST be callable outside a React component (Requirement 4.6), because
//    gstPreview() in AdminTakeOrder.js is an exported pure helper that produces a
//    display label. That rules out a hook-only design.
// 2. Changing language MUST re-render the tree, which money.js never needs
//    (currency is set once before first paint). Hence the subscriber set and the
//    useLanguage() hook at the bottom.
//
// INVARIANT — do not break this:
//   The singleton below is the source of truth for pure helpers. React state
//   (via useLanguage) exists ONLY to trigger re-render. They are always mutated
//   together, through setLanguage(). No component may set one without the other.
//
// INERTNESS (Requirement 1): a tenant with no `i18n` settings block never calls
// configureLanguages with a valid config, so _multilingual stays false, _active
// stays 'en', and every t() call returns the pre-feature English literal.

import { useEffect, useState } from 'react';

import {
  DEFAULT_LANGUAGE,
  LANGUAGE_CODES,
  isRegisteredLanguage,
} from './i18nRegistry';
import { TRANSLATIONS } from './i18nCatalog';

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let _active = DEFAULT_LANGUAGE;
let _available = [DEFAULT_LANGUAGE];
let _defaultLanguage = DEFAULT_LANGUAGE;
let _multilingual = false;

const _listeners = new Set();

// Diagnostics are deduplicated because a missing key inside a menu render loop
// would otherwise emit one warning per item per render and bury the console
// during service.
const _warned = new Set();

function _warnOnce(message) {
  if (_warned.has(message)) return;
  _warned.add(message);
  // eslint-disable-next-line no-console
  console.warn(`[i18n] ${message}`);
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

// Coerce any input to a registered language code (Requirement 5.8).
export function normalizeLanguage(code) {
  const c = String(code || '').trim().toLowerCase();
  return isRegisteredLanguage(c) ? c : DEFAULT_LANGUAGE;
}

/**
 * Apply the tenant's I18n_Config, as delivered in the `settings.i18n` block of
 * GET /admin/pos/take-order/data.
 *
 * The backend already normalizes this block, but this re-derives every field
 * defensively: the response may come from an older deployment, and a frontend
 * that trusts an unvalidated shape here would render a switcher for a language
 * it has no catalog for.
 *
 * Returns the resolved config so the caller can decide whether to read the
 * device store — a single-language tenant must not touch it (Requirement 1.3).
 */
export function configureLanguages(i18nConfig) {
  const cfg = i18nConfig && typeof i18nConfig === 'object' ? i18nConfig : null;

  const rawList = cfg && Array.isArray(cfg.availableLanguages) ? cfg.availableLanguages : [];
  const available = [];
  rawList.forEach((raw) => {
    const c = String(raw || '').trim().toLowerCase();
    if (isRegisteredLanguage(c) && !available.includes(c)) available.push(c);
  });
  if (!available.includes(DEFAULT_LANGUAGE)) available.unshift(DEFAULT_LANGUAGE);

  const rawDefault = cfg ? String(cfg.defaultLanguage || '').trim().toLowerCase() : '';
  const defaultLanguage = available.includes(rawDefault) ? rawDefault : DEFAULT_LANGUAGE;

  // Multilingual requires an explicit opt-in AND a real second language. One
  // language is not multilingual no matter what `enabled` claims.
  const multilingual = Boolean(cfg && cfg.enabled) && available.length >= 2;

  _available = multilingual ? available : [DEFAULT_LANGUAGE];
  _defaultLanguage = multilingual ? defaultLanguage : DEFAULT_LANGUAGE;
  _multilingual = multilingual;

  // Re-clamp the active language: a tenant may have just removed a language
  // that this device had selected (Requirement 3.5).
  _active = _available.includes(_active) ? _active : _defaultLanguage;

  // Always notify, even when the active language did not change. Loading the config
  // is itself a render-affecting event: it decides whether the language switcher and
  // the product-editor translation inputs appear at all. Subscribers that only
  // watched for a language CHANGE would never learn that multilingual turned on.
  _notify();

  return {
    multilingual: _multilingual,
    available: _available.slice(),
    defaultLanguage: _defaultLanguage,
    active: _active,
  };
}

// ---------------------------------------------------------------------------
// Active language
// ---------------------------------------------------------------------------

function _notify() {
  _listeners.forEach((fn) => {
    try {
      fn(_active);
    } catch {
      // One misbehaving subscriber must not stop the others from updating.
    }
  });
}

/**
 * Set the active language. Any code that is unregistered, or registered but not
 * offered by this tenant, is coerced to the TENANT default rather than throwing
 * (Requirements 3.5, 5.8) — a stale value in device storage must never leave the
 * screen unusable.
 *
 * Note the fallback target: the tenant's `defaultLanguage`, not the global
 * DEFAULT_LANGUAGE. Requirements 3.5 and 5.8 overlap for a code that is both
 * unregistered AND unavailable, and 5.8 read literally would send such a code to
 * English. Preferring the tenant default is the better reading: a restaurant that
 * deliberately set Tamil as its default should land on Tamil when a stale value
 * is discarded, not be silently switched to English. The two readings coincide
 * whenever the tenant default IS English, which is every tenant today.
 */
export function setLanguage(code) {
  const candidate = String(code || '').trim().toLowerCase();
  const next =
    isRegisteredLanguage(candidate) && _available.includes(candidate)
      ? candidate
      : _defaultLanguage;
  if (next === _active) return _active;
  _active = next;
  _notify();
  return _active;
}

export function getLanguage() {
  return _active;
}

export function getAvailableLanguages() {
  return _available.slice();
}

export function getDefaultLanguage() {
  return _defaultLanguage;
}

export function isMultilingual() {
  return _multilingual;
}

export function subscribe(listener) {
  if (typeof listener !== 'function') return () => {};
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

// ---------------------------------------------------------------------------
// Lookup
// ---------------------------------------------------------------------------

function _lookup(lang, key) {
  const table = TRANSLATIONS[lang];
  if (!table) return '';
  const value = table[key];
  return typeof value === 'string' && value !== '' ? value : '';
}

const _TOKEN_RE = /\{(\w+)\}/g;

function _interpolate(template, params, key) {
  if (template.indexOf('{') === -1) return template;
  const p = params && typeof params === 'object' ? params : {};
  return template.replace(_TOKEN_RE, (_match, token) => {
    const supplied = p[token];
    if (supplied === undefined || supplied === null) {
      // Empty rather than leaving a raw `{token}` on screen (Requirement 5.6).
      _warnOnce(`missing interpolation param "${token}" for key "${key}"`);
      return '';
    }
    return String(supplied);
  });
}

/**
 * Resolve a translation key against the active language.
 *
 * Resolution order (Requirements 5.2, 5.3):
 *   1. active language value, if non-empty
 *   2. default language (English) value, if non-empty
 *   3. the key itself, plus a one-time warning
 *
 * Step 3 returns the KEY rather than an empty string on purpose: an unlabelled
 * "Send to kitchen" button is not operable during service, whereas a visible
 * `takeOrder.send` is both operable and diagnosable. The catalog completeness
 * test (design Property 1) exists so step 3 is unreachable in a shipped build.
 */
export function t(key, params) {
  const k = String(key || '');
  if (!k) return '';

  let template = _lookup(_active, k);
  if (!template && _active !== DEFAULT_LANGUAGE) {
    template = _lookup(DEFAULT_LANGUAGE, k);
    if (template) _warnOnce(`missing "${_active}" translation for key "${k}"; used ${DEFAULT_LANGUAGE}`);
  }
  if (!template) {
    _warnOnce(`missing translation for key "${k}" in every language`);
    return k;
  }

  return _interpolate(template, params, k);
}

// ---------------------------------------------------------------------------
// React binding
// ---------------------------------------------------------------------------

/**
 * Subscribe a component to language changes. Returns the active code, so a
 * component can use it as a render key for values it derives itself.
 *
 * Callers must NOT use the returned value to key a subtree that holds the
 * in-progress ticket — remounting on language change would discard a waiter's
 * order mid-service (Requirement 3.8, design Property 10).
 */
export function useLanguage() {
  // Ticks on ANY notification rather than storing the language itself. Storing the
  // language would make React bail out of the re-render when configureLanguages
  // fires without changing the active code — which is exactly what happens when a
  // multilingual tenant's config arrives with defaultLanguage 'en', and would leave
  // the switcher and the translation inputs invisible until something else
  // re-rendered the page.
  const [, setTick] = useState(0);
  useEffect(() => subscribe(() => setTick((n) => n + 1)), []);
  return getLanguage();
}

// Test-only reset. Module state is a singleton, so a test that configures a
// multilingual tenant would otherwise leak into the next test.
export function __resetForTests() {
  _active = DEFAULT_LANGUAGE;
  _available = [DEFAULT_LANGUAGE];
  _defaultLanguage = DEFAULT_LANGUAGE;
  _multilingual = false;
  _listeners.clear();
  _warned.clear();
}

export { DEFAULT_LANGUAGE, LANGUAGE_CODES };
